import { useEffect, useMemo } from 'react';
import useLocalStorageState from '../../../utils/useLocalStorageState';
import {
    FILTER_TYPES,
    isAppliedFilterValue,
    renderAppliedFiltersDescription,
} from '../../../utils/filterUtils';
import StatusPill from '../../Components/Invoicing/StatusPill';

const useInvoicingFilters = (storageKey = '') => {
    const { state: filters, setValue: setFilters } = useLocalStorageState(`${storageKey}Filters`);
    const handleApplyFilter = (newFilters) => setFilters(newFilters);

    const filtersSetup = useMemo(() => {
        let defaultFilters = [
            {
                name: 'types',
                label: 'Types',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options:
                        storageKey === 'invoiceManagement'
                            ? [
                                  'Time',
                                  'Expense',
                                  'Time & Expense',
                                  'Deposits',
                                  'Credit Memos',
                                  'Perm Fees',
                                  'Finance Fee',
                                  'Corrections',
                                  'High Priority',
                              ]
                            : ['General', 'RT', 'Deposits', 'Credit Memo', 'High Priority'],
                    emptyLabel: 'All',
                },
            },
        ];

        if (storageKey === 'invoiceManagement') {
            defaultFilters.push({
                name: 'transactionDateRange',
                label: 'Transaction Date Range',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            });
        }

        defaultFilters = defaultFilters.concat([
            {
                name: 'invoiceDate',
                label: 'Invoice Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'postingDate',
                label: 'Posting Date',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            },
            {
                name: 'frequency',
                label: 'Frequency',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                overrides: {
                    options: ['Weekly', 'Monthly', 'Bi-Weekly', '15 Days After Month-End '],
                    emptyLabel: 'All',
                },
            },
        ]);

        if (storageKey === 'invoiceInitiation') {
            defaultFilters.push({
                name: 'timesheetLineDateRange',
                label: 'Timesheet Line Date Range',
                type: FILTER_TYPES.DATE_RANGE,
                defaultValue: { from: null, to: null },
            });
        }

        if (storageKey === 'invoiceManagement') {
            defaultFilters.push({
                name: 'status',
                label: 'Statuses',
                type: FILTER_TYPES.CHECKBOX,
                defaultValue: [],
                isQuickFilter: true,
                overrides: {
                    options: ['PreInvoiced', 'Invoiced', 'Error', 'Abandoned', 'Outdated'],
                    emptyLabel: 'All',
                    render: (value) => <StatusPill status={value} />,
                },
            });
        }

        return defaultFilters;
    }, []);

    useEffect(() => {
        if (filters) return;
        // If filters are not set, set them to the default values
        const defaultFiltersValue = {};
        filtersSetup.forEach(({ name, defaultValue }) => {
            if (isAppliedFilterValue(defaultValue)) {
                defaultFiltersValue[name] = defaultValue;
            }
        });
        if (Object.keys(defaultFiltersValue).length > 0) {
            setFilters(defaultFiltersValue);
        }
    }, [filtersSetup]);

    const appliedFiltersDescription = useMemo(
        () => renderAppliedFiltersDescription(filters, filtersSetup),
        [filters, filtersSetup]
    );

    if (!storageKey) {
        throw new Error(
            'Storage key is required and should be unique to prevent overwriting data.'
        );
    }

    return {
        filters,
        setFilters,
        filtersSetup,
        handleApplyFilter,
        isLoadingFilterConfig: false,
        appliedFiltersDescription,
    };
};

export default useInvoicingFilters;
